/**
 * HeroIllustration
 * Inline SVG: a chat bubble (left) wired into a semantic graph (center)
 * with an audit log strip (bottom right). Themed via CSS vars — currentColor
 * pulls from text-foreground; primary/accent pulled directly.
 *
 * No raster assets, no layout shift.
 */
export function HeroIllustration() {
  return (
    <div className="relative aspect-[5/4] w-full">
      <svg
        viewBox="0 0 500 400"
        role="img"
        aria-label="Sema hero illustration — a chat conversation grounded in a semantic graph with an audit log."
        className="h-full w-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="sema-stroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="hsl(var(--accent))" />
            <stop offset="100%" stopColor="hsl(var(--primary))" />
          </linearGradient>
          <radialGradient id="sema-node-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--accent))" stopOpacity="0.55" />
            <stop offset="100%" stopColor="hsl(var(--accent))" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Background frame */}
        <rect
          x="0.5"
          y="0.5"
          width="499"
          height="399"
          rx="20"
          fill="hsl(var(--card))"
          stroke="hsl(var(--border))"
        />

        {/* === Chat bubble (left) === */}
        <g transform="translate(28 36)">
          <rect
            width="190"
            height="120"
            rx="12"
            fill="hsl(var(--background))"
            stroke="hsl(var(--border))"
          />
          <text
            x="14"
            y="26"
            fontSize="10"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--muted-foreground))"
            letterSpacing="0.15em"
          >
            YOU
          </text>
          <text
            x="14"
            y="50"
            fontSize="12"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--foreground))"
          >
            What drove churn
          </text>
          <text
            x="14"
            y="66"
            fontSize="12"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--foreground))"
          >
            in Q4 enterprise?
          </text>
          <line x1="14" y1="80" x2="176" y2="80" stroke="hsl(var(--border))" />
          <circle cx="22" cy="98" r="3" fill="hsl(var(--accent))" />
          <text
            x="32"
            y="102"
            fontSize="10"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--muted-foreground))"
          >
            grounded · 3 entities
          </text>
        </g>

        {/* Chat tail */}
        <path
          d="M210 110 L228 124 L210 138 Z"
          fill="hsl(var(--background))"
          stroke="hsl(var(--border))"
        />

        {/* === Semantic graph (center / right) === */}
        <g transform="translate(250 30)">
          {/* edges */}
          <g stroke="url(#sema-stroke)" strokeWidth="1.25" fill="none">
            <path d="M60 70 L160 30" />
            <path d="M60 70 L150 130" />
            <path d="M60 70 L40 170" />
            <path d="M160 30 L160 130" />
            <path d="M40 170 L150 130" />
          </g>

          {/* node halos */}
          <circle cx="60" cy="70" r="34" fill="url(#sema-node-glow)" />
          <circle cx="160" cy="30" r="22" fill="url(#sema-node-glow)" />
          <circle cx="150" cy="130" r="22" fill="url(#sema-node-glow)" />
          <circle cx="40" cy="170" r="22" fill="url(#sema-node-glow)" />

          {/* primary node — "Customer" */}
          <g>
            <circle
              cx="60"
              cy="70"
              r="22"
              fill="hsl(var(--background))"
              stroke="hsl(var(--accent))"
              strokeWidth="1.5"
            />
            <text
              x="60"
              y="74"
              textAnchor="middle"
              fontSize="10"
              fontFamily="JetBrains Mono, monospace"
              fill="hsl(var(--foreground))"
            >
              customer
            </text>
          </g>

          {/* secondary nodes */}
          {[
            { x: 160, y: 30, label: "plan" },
            { x: 160, y: 130, label: "invoice" },
            { x: 40, y: 170, label: "event" },
          ].map((n) => (
            <g key={n.label}>
              <circle
                cx={n.x}
                cy={n.y}
                r="16"
                fill="hsl(var(--background))"
                stroke="hsl(var(--primary))"
                strokeWidth="1.25"
              />
              <text
                x={n.x}
                y={n.y + 3}
                textAnchor="middle"
                fontSize="9"
                fontFamily="JetBrains Mono, monospace"
                fill="hsl(var(--muted-foreground))"
              >
                {n.label}
              </text>
            </g>
          ))}

          {/* edge labels */}
          <text
            x="110"
            y="46"
            fontSize="8"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--muted-foreground))"
          >
            has_plan
          </text>
          <text
            x="100"
            y="115"
            fontSize="8"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--muted-foreground))"
          >
            billed_via
          </text>
          <text
            x="40"
            y="130"
            fontSize="8"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--muted-foreground))"
          >
            emits
          </text>
        </g>

        {/* === Audit log (bottom) === */}
        <g transform="translate(28 220)">
          <rect
            width="444"
            height="150"
            rx="12"
            fill="hsl(var(--background))"
            stroke="hsl(var(--border))"
          />
          <text
            x="16"
            y="26"
            fontSize="10"
            fontFamily="JetBrains Mono, monospace"
            fill="hsl(var(--muted-foreground))"
            letterSpacing="0.15em"
          >
            AUDIT · QUERY 0xA1F2
          </text>
          <line x1="0" y1="40" x2="444" y2="40" stroke="hsl(var(--border))" />

          {[
            { y: 60, k: "graph", v: "customer → invoice → event", ok: true },
            { y: 82, k: "sql", v: "SELECT … WHERE plan_id IN (…)", ok: true },
            { y: 104, k: "rows", v: "412 (sampled), 0 PII fields", ok: true },
            { y: 126, k: "policy", v: "row_acl: tier=enterprise", ok: true },
          ].map((row, i) => (
            <g key={i}>
              <circle cx="22" cy={row.y - 4} r="3" fill="hsl(var(--accent))" />
              <text
                x="34"
                y={row.y}
                fontSize="10"
                fontFamily="JetBrains Mono, monospace"
                fill="hsl(var(--muted-foreground))"
              >
                {row.k}
              </text>
              <text
                x="100"
                y={row.y}
                fontSize="10"
                fontFamily="JetBrains Mono, monospace"
                fill="hsl(var(--foreground))"
              >
                {row.v}
              </text>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
}
